import { notification } from "antd";
import { makeAutoObservable, runInAction } from "mobx";
import { b2bErrorText, onB2BError, toB2BError } from "@/utils/b2bErrors";
// FR-006: карточка компании (UC1112–UC1114). Backs CompanyPage и вкладки settings / tariffs.
export class SingleCompanyStore {
    constructor(companyService, companyFinanceService) {
        this.companyService = companyService;
        this.companyFinanceService = companyFinanceService;
        this.companyId = null;
        this.company = null;
        this.isLoading = false;
        this.notFound = false;
        /** Ошибка загрузки карточки — показывается с кнопкой «Повторить» вместо бесконечного скелета. */
        this.loadError = null;
        this.isSaving = false;
        /** Ошибка создания компании — показывается у формы, форма не сбрасывается. */
        this.createError = null;
        this.finance = null;
        this.financeLoading = false;
        this.financeSaving = false;
        this.tariffPair = null;
        this.tariffLoading = false;
        this.tariffSaving = false;
        /** Ошибка загрузки тарифа — отличается от «у компании нет тарифа». */
        this.tariffError = null;
        this.generation = 0;
        makeAutoObservable(this, { generation: false });
    }
    async init(companyId) {
        if (this.companyId === companyId && this.company)
            return;
        this.dispose();
        this.companyId = companyId;
        await this.load();
    }
    async load() {
        const companyId = this.companyId;
        if (!companyId)
            return;
        const generation = this.generation;
        this.isLoading = true;
        this.loadError = null;
        try {
            const company = await this.companyService.getCompany(companyId);
            runInAction(() => {
                if (generation !== this.generation)
                    return;
                this.company = company;
                this.notFound = company === null;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить компанию");
            runInAction(() => {
                if (generation === this.generation) {
                    this.loadError = b2bErrorText(error, "Не удалось загрузить компанию");
                }
            });
        }
        finally {
            runInAction(() => {
                if (generation === this.generation)
                    this.isLoading = false;
            });
        }
    }
    /**
     * Перечитать карточку без индикатора загрузки (после оплаты/сторно/корректировки, WP-DSP-3-FIX):
     * `credit_state` из `v_corp_companies` меняется вместе с балансом — бейджи «Лимит исчерпан» обновляются без reload.
     */
    async refreshCompanyQuietly() {
        const companyId = this.companyId;
        if (!companyId)
            return;
        const generation = this.generation;
        try {
            const company = await this.companyService.getCompany(companyId);
            runInAction(() => {
                if (generation === this.generation && company)
                    this.company = company;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось обновить карточку компании");
        }
    }
    /** «Обновить» (BR-UI-05): шапка и уже открытые данные вкладок. */
    async refresh() {
        await this.load();
        if (this.finance)
            await this.loadFinance();
        if (this.tariffPair)
            await this.loadTariffPair();
    }
    clearCreateError() {
        this.createError = null;
    }
    async createCompany(requisites) {
        this.isSaving = true;
        this.createError = null;
        try {
            return await this.companyService.createCompany(requisites);
        }
        catch (error) {
            runInAction(() => {
                this.createError = b2bErrorText(error, "Не удалось создать компанию", "companyRequisites");
            });
            return null;
        }
        finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
    async updateRequisites(requisites) {
        const companyId = this.companyId;
        if (!companyId)
            return false;
        this.isSaving = true;
        try {
            await this.companyService.updateRequisites(companyId, requisites);
            await this.load();
            return true;
        }
        catch (error) {
            onB2BError(error, "Не удалось сохранить реквизиты", "companyRequisites");
            return false;
        }
        finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
    /** Только ADMINISTRATOR: вызывается финансовым блоком, который у DISPATCHER не монтируется (D14). */
    async loadFinance() {
        const companyId = this.companyId;
        if (!companyId)
            return;
        const generation = this.generation;
        this.financeLoading = true;
        try {
            const finance = await this.companyFinanceService.getFinance(companyId);
            runInAction(() => {
                if (generation === this.generation)
                    this.finance = finance;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить финансовые настройки");
        }
        finally {
            runInAction(() => {
                if (generation === this.generation)
                    this.financeLoading = false;
            });
        }
    }
    async updateFinance(patch) {
        const companyId = this.companyId;
        if (!companyId || Object.keys(patch).length === 0)
            return false;
        this.financeSaving = true;
        try {
            await this.companyFinanceService.updateFinance(companyId, patch);
            await this.loadFinance();
            return true;
        }
        catch (error) {
            onB2BError(error, "Не удалось сохранить финансовые настройки", "companyRequisites");
            return false;
        }
        finally {
            runInAction(() => {
                this.financeSaving = false;
            });
        }
    }
    async setStatus(status, reason) {
        const companyId = this.companyId;
        if (!companyId)
            return null;
        this.isSaving = true;
        try {
            const result = await this.companyService.setStatus(companyId, status, reason);
            await this.load();
            return result;
        }
        catch (error) {
            if (toB2BError(error).symbol === "CORP_COMPANY_HAS_OPEN_ORDERS") {
                // CP305: число незавершённых корп-заказов — из v_corp_companies.open_orders_count (UC1113 A3)
                await this.load();
                const openOrders = this.company?.openOrdersCount ?? 0;
                notification.error({
                    message: "Архивировать нельзя",
                    description: `У компании есть незавершённые корпоративные заказы (${openOrders}). Дождитесь их завершения или отмены.`,
                });
            }
            else {
                onB2BError(error, "Не удалось сменить статус компании");
            }
            return null;
        }
        finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
    async loadTariffPair() {
        const companyId = this.companyId;
        if (!companyId)
            return;
        const generation = this.generation;
        this.tariffLoading = true;
        this.tariffError = null;
        try {
            const pair = await this.companyService.getTariffPair(companyId);
            runInAction(() => {
                if (generation === this.generation)
                    this.tariffPair = pair;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить тариф компании");
            runInAction(() => {
                if (generation === this.generation) {
                    this.tariffError = b2bErrorText(error, "Не удалось загрузить тариф компании");
                }
            });
        }
        finally {
            runInAction(() => {
                if (generation === this.generation)
                    this.tariffLoading = false;
            });
        }
    }
    /** Только ADMINISTRATOR (D14): `corp_tariff_pair_upsert`. */
    async saveTariffPair(pair) {
        const companyId = this.companyId;
        if (!companyId)
            return false;
        this.tariffSaving = true;
        try {
            await this.companyService.upsertTariffPair(companyId, pair);
            await this.loadTariffPair();
            return true;
        }
        catch (error) {
            onB2BError(error, "Не удалось сохранить тариф компании");
            return false;
        }
        finally {
            runInAction(() => {
                this.tariffSaving = false;
            });
        }
    }
    dispose() {
        this.generation += 1;
        this.companyId = null;
        this.company = null;
        this.isLoading = false;
        this.notFound = false;
        this.loadError = null;
        this.isSaving = false;
        this.createError = null;
        this.finance = null;
        this.financeLoading = false;
        this.financeSaving = false;
        this.tariffPair = null;
        this.tariffLoading = false;
        this.tariffSaving = false;
        this.tariffError = null;
    }
}
